<template>
  <FlotoCrudContainer
    ref="list"
    :fetch-fn="getAuditItems"
    :scrollable="false"
    :resource-name="$t('audit_trail')"
    :as-table="asTable"
    :columns="tableColumns"
  >
    <template v-slot:add-controls="{ refreshList, items }">
      <MRow class="items-center" :gutter="0">
        <div class="flex flex-1 items-center">
          <FlotoForm
            ref="formRef"
            class="flex items-center"
            @submit="refreshList"
          >
            <MCol auto-size class="pr-2">
              <FlotoFormItem
                id="from-picker"
                :label="$t('from')"
                :rules="{
                  // eslint-disable-next-line
                  max_date: endDate,
                }"
              >
                <FlotoDatePicker
                  v-model="startDate"
                  :allow-clear="false"
                  :max-date="endDate"
                />
              </FlotoFormItem>
            </MCol>
            <MCol auto-size class="px-2">
              <FlotoFormItem
                id="to-picker"
                :rules="{
                  // eslint-disable-next-line
                  min_date: startDate,
                }"
                :label="$t('to')"
              >
                <FlotoDatePicker
                  v-model="endDate"
                  :allow-clear="false"
                  :min-date="startDate"
                />
              </FlotoFormItem>
            </MCol>
            <MCol v-if="applyComponentTypeFilter" auto-size class="px-2">
              <FlotoFormItem id="type-picker" :label="$t('type')">
                <FlotoDropdownPicker
                  v-model="componentType"
                  :allow-clear="false"
                  as-input
                  :options="componentTypeOptions"
                />
              </FlotoFormItem>
            </MCol>
            <MCol v-if="applyDiscoveryMethodFilter" auto-size class="px-2">
              <FlotoFormItem id="agent-picker" :label="$t('agent')">
                <FlotoDropdownPicker
                  v-model="discoveryMethod"
                  :allow-clear="false"
                  as-input
                  :options="discoveryMethodOptions"
                />
              </FlotoFormItem>
            </MCol>
            <template v-slot:submit>
              <span />
            </template>
          </FlotoForm>

          <MButton
            id="search-btn"
            class="mt-2 ml-3"
            :loading="searchLoading"
            @click="submitForm"
          >
            {{ $t('search') }}
          </MButton>
          <MCol class="text-right">
            <ExportButton
              v-if="(items || []).length > 0"
              :button-text="$t('download')"
              class="mx-1"
              :header="`${$t('download')}`"
              :file-name="fileName"
              :options="[
                { text: $t('pdf'), value: 'pdf' },
                { text: $t('excel'), value: 'excel' },
                { text: $t('csv'), value: 'csv' },
              ]"
              :default-data="{ exportFormat: 'pdf' }"
              :download-fn="handleDownload"
            >
              <template v-slot:trigger>
                <MTooltip>
                  <template v-slot:trigger>
                    <MButton
                      shape="circle"
                      variant="neutral-lighter"
                      :shadow="false"
                    >
                      <MIcon name="download" />
                    </MButton>
                  </template>
                  {{ $tc('download') }}
                </MTooltip>
              </template>
            </ExportButton>
          </MCol>
        </div>
        <MDivider />
      </MRow>
    </template>
    <template v-if="!asTable" v-slot:list-items="{ items }">
      <component
        :is="type === 'change_log' ? 'PropertyComponentProvider' : 'div'"
      >
        <MRow>
          <MCol class="my-2">
            <MTimeline mode="left" class="pl-2">
              <Audit
                v-for="timeline in items"
                :key="timeline.id"
                :parent-id="resourceId"
                :module-name="moduleName"
                v-bind="timeline"
              >
                <template v-if="type === 'change_log'" v-slot:action>
                  <FlotoDrawer
                    :open="currentSelectedItemId === timeline.id"
                    class="flex"
                    @hide="currentSelectedItemId = null"
                  >
                    <template v-slot:title>
                      {{ $tc('view') }} {{ $tc(timeline.type) }}
                    </template>
                    <template v-slot:trigger>
                      <a @click="currentSelectedItemId = timeline.id">
                        <small>{{ $t('view') }} {{ $tc('change', 2) }}</small>
                      </a>
                    </template>
                    <ViweChangeLog
                      :type="timeline.type"
                      :component-id="timeline.componentId"
                      :action-type="timeline.actionType"
                      :component-details-id="timeline.jsonDataId"
                      :module-name="moduleName"
                    />
                    <template v-slot:actions="{ hide }">
                      <MButton id="done-btn" variant="default" @click="hide">
                        {{ $t('done') }}
                      </MButton>
                    </template>
                  </FlotoDrawer>
                </template>
              </Audit>
            </MTimeline>
          </MCol>
        </MRow>
      </component>
    </template>
    <!-- $modules.getModuleRoute('asset-management', 'asset-movement') -->
    <template v-slot:name="{ item }">
      <td class="text-ellipsis">
        <ResourceListTitle
          :name="item.name"
          :link="
            $modules.getModuleRoute('asset-management', 'movement-view', {
              params: { id: item.id },
            })
          "
        />
      </td>
    </template>
    <template v-slot:requesterId="{ item }">
      <td class="text-ellipsis">
        <FlotoRequesterPicker
          :value="item.requesterId"
          disabled
          placeholder="---"
        />
      </td>
    </template>
    <template v-slot:referenceNumber="{ item }">
      <td class="text-ellipsis">{{
        item.referenceNumber ? item.referenceNumber : '---'
      }}</td>
    </template>
    <template v-slot:movementDate="{ item }">
      <td class="text-ellipsis">
        {{ !item.movementDate ? '---' : item.movementDate | datetime }}
      </td>
    </template>
    <template v-slot:expectedDate="{ item }">
      <td class="text-ellipsis">
        {{ !item.expectedDate ? '---' : item.expectedDate | datetime }}
      </td>
    </template>
    <template v-slot:returnDate="{ item }">
      <td class="text-ellipsis">
        {{ !item.returnDate ? '---' : item.returnDate | datetime }}
      </td>
    </template>
    <template v-slot:movementType="{ item }">
      <td class="text-ellipsis">
        <AssetMovementTypePicker
          :value="item.movementType"
          disabled
          placeholder="---"
        />
      </td>
    </template>
    <template v-slot:statusId="{ item }">
      <td class="text-ellipsis">
        <FlotoStatusPicker
          :value="item.statusId"
          :module-name="$constants.ASSET_MOVEMENT"
          disabled
          placeholder="---"
        />
      </td>
    </template>
    <template v-slot:fromLocation="{ item }">
      <td class="text-ellipsis">
        <FlotoLocationPicker
          disabled
          :value="item.fromLocation"
          placeholder="---"
          :as-input="false"
        />
      </td>
    </template>
    <template v-slot:toLocation="{ item }">
      <td class="text-ellipsis">
        <FlotoLocationPicker
          disabled
          :value="item.toLocation"
          placeholder="---"
          :as-input="false"
        />
      </td>
    </template>
    <template v-slot:fromDepartment="{ item }">
      <td class="text-ellipsis">
        <FlotoDepartmentPicker
          disabled
          :value="item.fromDepartment"
          placeholder="---"
          :as-input="false"
        />
      </td>
    </template>
    <template v-slot:toDepartment="{ item }">
      <td class="text-ellipsis">
        <FlotoDepartmentPicker
          disabled
          :value="item.toDepartment"
          placeholder="---"
          :as-input="false"
        />
      </td>
    </template>
    <template v-slot:returnableType="{ item }">
      <td class="text-ellipsis">
        <AssetMovementReturnableTypePicker
          :value="item.returnableType"
          :type="item.movementType"
          :placeholder="disabled ? '---' : undefined"
          disabled
        />
      </td>
    </template>
    <template v-slot:vendorId="{ item }">
      <td class="text-ellipsis">
        <VendorPicker
          disabled
          :value="item.vendorId"
          placeholder="---"
          :as-input="false"
        />
      </td>
    </template>
    <template v-slot:inWarranty="{ item }">
      <td class="text-ellipsis">
        {{ item.inWarranty === true ? $tc('yes') : $tc('no') }}
      </td>
    </template>
    <template v-slot:repairType="{ item }">
      <td class="text-ellipsis">
        <!-- {{ item.repairType ? item.repairType : '---' }} -->
        <AssetMovementRepairTypePicker
          :value="item.repairType"
          placeholder="---"
          disabled
        />
      </td>
    </template>
    <template v-slot:repairCost="{ item }">
      <td class="text-ellipsis">
        {{ item.repairCost ? item.repairCost : '---' }}
      </td>
    </template>
    <template v-slot:warrantyCovered="{ item }">
      <td class="text-ellipsis">
        {{ item.warrantyCovered === true ? $tc('yes') : $tc('no') }}
      </td>
    </template>
    <template v-slot:replaced="{ item }">
      <td class="text-ellipsis">
        {{ item.replaced === true ? $tc('yes') : $tc('no') }}
      </td>
    </template>
    <template v-slot:replaceBy="{ item }">
      <td class="text-ellipsis">
        <!-- {{ item.replaceBy ? item.replaceBy : '---' }} -->
        <span v-if="item.replaceBy === 0">---</span>
        <span v-else>
          <AssetPickerDrawer
            v-if="item.replaceBy"
            :value="item.replaceBy"
            disabled
            placeholder="---"
            :as-input="false"
            :module-name="item.refModel"
            :fetch-fn="fetchAssetData"
          />
        </span>
      </td>
    </template>
    <template v-slot:createdTime="{ item }">
      <td class="text-ellipsis">
        {{ !item.createdTime ? '---' : item.createdTime | datetime }}
      </td>
    </template>
    <template v-slot:executionSource="{ item }">
      <td class="text-ellipsis">
        {{ item.executionSource || '---' }}
      </td>
    </template>
    <template v-slot:wolStatus="{ item }">
      <td class="text-ellipsis">
        {{ $tc(item.wolStatus) || '---' }}
      </td>
    </template>
    <template v-slot:remark="{ item }">
      <td class="text-ellipsis">
        {{ item.remark || '---' }}
      </td>
    </template>
    <template v-slot:updatedTime="{ item }">
      <td class="text-ellipsis">
        {{ !item.updatedTime ? '---' : item.updatedTime | datetime }}
      </td>
    </template>
    <template v-slot:actions="{ item }">
      <td class="text-ellipsis">
        <div style="display: inline-flex">
          <RepairHistoryViewDrawer :history="item" />
        </div>
      </td>
    </template>
    <template v-slot:assetStartTime="{ item }">
      <td class="text-ellipsis">
        {{ !item.assetStartTime ? '---' : item.assetStartTime | datetime }}
      </td>
    </template>
    <template v-slot:assetShutDownTime="{ item }">
      <td class="text-ellipsis">
        {{
          !item.assetShutDownTime ? '---' : item.assetShutDownTime | datetime
        }}
      </td>
    </template>
    <template v-slot:upTimeDuration="{ item }">
      <td class="text-ellipsis">
        {{
          !item.upTimeDuration
            ? '---'
            : calculateDuration(item, 'upTimeDuration')
        }}
      </td>
    </template>
    <template v-slot:downTimeDuration="{ item }">
      <td class="text-ellipsis">
        {{
          !item.downTimeDuration
            ? '---'
            : calculateDuration(item, 'downTimeDuration')
        }}
      </td>
    </template>
  </FlotoCrudContainer>
</template>

<script>
import AssetMovementRepairTypePicker from '@components/data-picker/asset-movement-repair-type-picker'
import AssetPickerDrawer from '@components/data-picker/asset-picker-drawer'
import { searchAssetsApi } from '@modules/asset/asset-api'
import ViweChangeLog from './view-change-log'
import Moment from 'moment'
import Audit from '@components/audit/audit'
import ResourceListTitle from '@components/resource/resource-list-title'
import AssetMovementTypePicker from '@components/data-picker/asset-movement-type-picker'
import MovementStatusPicker from '@components/data-picker/movement-status-picker'
import PropertyComponentProvider from '../propery-component-list/property-component-provider'
import AssetMovementReturnableTypePicker from '@components/data-picker/asset-movement-returnable-time-picker'
import VendorPicker from '@components/data-picker/vendor-picker'
import RepairHistoryViewDrawer from './repair-history-view-drawer'
import { getEncryptedPassword } from '@utils/password'
import { downloadApi } from '@src/modules/reports/reports-api'
import ExportButton from '@modules/reports/components/export-button'
import {
  buildRelationalQualificationStructure,
  buildFlatQualificationStructure,
} from '@data/qualification'
import Pick from 'lodash/pick'
import { PreferenceComputed } from '@state/modules/preference'

export default {
  name: 'HistoryListContainer',
  components: {
    Audit,
    ViweChangeLog,
    PropertyComponentProvider,
    ResourceListTitle,
    AssetMovementTypePicker,
    MovementStatusPicker,
    AssetMovementReturnableTypePicker,
    VendorPicker,
    AssetPickerDrawer,
    RepairHistoryViewDrawer,
    AssetMovementRepairTypePicker,
    ExportButton,
  },
  props: {
    resourceId: { type: Number, required: true },
    moduleName: { type: String, required: true },
    fetchFn: { type: Function, default: undefined },
    type: { type: String, required: true },
    applyComponentTypeFilter: { type: Boolean, default: false },
    applyDiscoveryMethodFilter: { type: Boolean, default: false },
    isSnmpAsset: { type: Boolean, default: false },
    isMobileDeviceAsset: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  data() {
    this.componentTypeOptions = [
      { text: this.$tc('hardware'), key: 'hardware' },
      ...(!this.isSnmpAsset
        ? [{ text: this.$tc('software'), key: 'software' }]
        : []),
      ...(!this.isSnmpAsset && !this.isMobileDeviceAsset
        ? [{ text: this.$tc('user'), key: 'user' }]
        : []),
    ]
    this.discoveryMethodOptions = [
      { text: this.__t('all'), key: 'all' },
      { text: this.__t('agent_less'), key: 'agent_less' },
      { text: this.__t('agent_based'), key: 'agent_based' },
    ]
    return {
      currentSelectedItemId: null,
      searchLoading: false,
      startDate:
        ['repair_history', 'movement_history'].indexOf(this.type) >= 0
          ? Moment().subtract(3, 'months').startOf('day').unix() * 1000
          : Moment().subtract(6, 'months').startOf('day').unix() * 1000,
      endDate:
        ['repair_history', 'movement_history'].indexOf(this.type) >= 0
          ? Moment().add(3, 'months').endOf('day').unix() * 1000
          : Moment().endOf('day').unix() * 1000,
      componentType: 'hardware',
      discoveryMethod: 'all',
    }
  },
  computed: {
    asTable() {
      return (
        [
          'movement_history',
          'repair_history',
          'wol_history',
          'asset_utilization_history',
        ].indexOf(this.type) >= 0
      )
    },
    tableColumns() {
      if (this.type === 'movement_history') {
        return [
          { name: `${this.$t('id')}`, key: 'name' },
          {
            name: `${this.$tc('requester')}`,
            key: 'requesterId',
          },
          {
            name: `${this.$tc('movement')} ${this.$tc('date')}`,
            key: 'movementDate',
          },
          {
            name: `${this.$tc('movement')} ${this.$tc('type')}`,
            key: 'movementType',
          },
          {
            name: `${this.$tc('movement')} ${this.$tc('status')}`,
            key: 'statusId',
          },
          {
            name: `${this.$tc('from')} ${this.$tc('location')}`,
            key: 'fromLocation',
          },
          {
            name: `${this.$tc('to')} ${this.$tc('location')}`,
            key: 'toLocation',
          },
          {
            name: `${this.$tc('from')} ${this.$tc('department')}`,
            key: 'fromDepartment',
          },
          {
            name: `${this.$tc('to')} ${this.$tc('department')}`,
            key: 'toDepartment',
          },
          {
            name: `${this.$tc('returnable_type')}`,
            key: 'returnableType',
          },
        ]
      }
      if (this.type === 'repair_history') {
        return [
          {
            name: `${this.$tc('requester')}`,
            key: 'requesterId',
          },
          {
            name: `${this.$tc('reference')} ${this.$tc('number')}`,
            key: 'referenceNumber',
          },
          {
            name: `${this.$tc('vendor')}`,
            key: 'vendorId',
          },
          {
            name: `${this.$tc('sent')} ${this.$tc('date')}`,
            key: 'movementDate',
          },
          {
            name: `${this.$tc('expected_return_date')}`,
            key: 'expectedDate',
          },
          {
            name: `${this.$tc('in_warranty')}`,
            key: 'inWarranty',
          },
          {
            name: `${this.$tc('return_date')}`,
            key: 'returnDate',
          },
          {
            name: `${this.$tc('repair_type')}`,
            key: 'repairType',
          },
          {
            name: `${this.$tc('repair')} ${this.$tc('cost')}`,
            key: 'repairCost',
          },
          {
            name: `${this.$tc('warranty_covered')}`,
            key: 'warrantyCovered',
          },
          {
            name: `${this.$tc('replaced')}`,
            key: 'replaced',
          },
          {
            name: `${this.$tc('replaced_by')}`,
            key: 'replaceBy',
          },
          {
            name: `${this.$tc('action')}`,
            key: 'actions',
          },
        ]
      }
      if (this.type === 'wol_history') {
        return [
          {
            name: `${this.$tc('history')} ${this.$tc('date')}`,
            key: 'createdTime',
          },
          {
            name: `${this.$tc('source')}`,
            key: 'executionSource',
          },
          {
            name: `${this.$tc('wol_status')}`,
            key: 'wolStatus',
          },
          {
            name: `${this.$tc('wol_remarks')}`,
            key: 'remark',
          },
          {
            name: `${this.$tc('last_updated_date')}`,
            key: 'updatedTime',
          },
        ]
      }
      if (this.type === 'asset_utilization_history') {
        return [
          {
            name: `${this.$tc('systme_startup_time')}`,
            key: 'assetStartTime',
          },
          {
            name: `${this.$tc('system_shutdown_time')}`,
            key: 'assetShutDownTime',
          },
          {
            name: `${this.$tc('up_time_duration')}`,
            key: 'upTimeDuration',
          },
          {
            name: `${this.$tc('down_time_duration')}`,
            key: 'downTimeDuration',
          },
          {
            name: `${this.$tc('last_updated_date')}`,
            key: 'updatedTime',
          },
        ]
      }
      return []
    },
    historyTabModuleName() {
      return {
        movement_history: this.$constants.ASSET_MOVEMENT,
        repair_history: this.$constants.ASSET_REPAIR_HISTORY,
        wol_history: this.$constants.WOL_HISTORY,
        asset_utilization_history: this.$constants.ASSET_USAGE_DETAILS,
        change_log: this.$constants.ASSET_CHANGE_LOG,
        scan_history: this.$constants.ASSET_SCAN_AUDIT,
        audit_trail: this.$constants.AUDIT,
      }
    },
    fileName() {
      let name = ''
      if (this.type === 'movement_history') {
        name = `${this.$tc('movement')} ${this.$tc('history')}`
      }

      if (this.type === 'repair_history') {
        name = `${this.$tc('repair')} ${this.$tc('history')}`
      }

      if (this.type === 'wol_history') {
        name = `${this.$tc('wol_history')}`
      }

      if (this.type === 'asset_utilization_history') {
        name = `${this.$tc('asset_utilization_history')}`
      }

      if (this.type === 'change_log') {
        name = `${this.$tc('change_log')}`
      }

      if (this.type === 'scan_history') {
        name = `${this.$tc('scan_history')}`
      }

      if (this.type === 'audit_trail') {
        name = `${this.$tc(this.moduleName)} ${this.$t('audit_trail')}`
      }

      return name
    },
    ...Pick(PreferenceComputed, ['attachmentPassword']),
  },
  methods: {
    fetchAssetData(moduleName, searchCriterias, limit, offset) {
      return searchAssetsApi(moduleName, searchCriterias, limit, offset)
    },
    submitForm() {
      if (this.searchLoading) {
        return
      }
      this.$refs.formRef.submit()
    },
    getAuditItems(limit, offset) {
      this.searchLoading = true
      if (this.fetchFn) {
        return this.fetchFn(
          {
            startDate: this.startDate,
            endDate: this.endDate,
            ...(this.applyComponentTypeFilter
              ? { componentType: this.componentType }
              : {}),
            ...(this.applyDiscoveryMethodFilter
              ? { discoveryMethod: this.discoveryMethod }
              : {}),
          },
          this.moduleName,
          this.resourceId,
          limit,
          offset
        ).finally(() => (this.searchLoading = false))
      } else {
        throw new Error('no fetch function found')
      }
    },
    refresh() {
      this.$refs.list.refresh()
    },
    calculateDuration(item, key) {
      if (item[key]) {
        const duration = this.$options.filters.duration(
          item[key],
          undefined,
          false
        )
        return `${duration}`
      }
      return 0
    },
    getAvailableAttachmentPassword(data) {
      const availableAttachmentPassword = data.protectedPassword
        ? getEncryptedPassword(data.protectedPassword)
        : getEncryptedPassword(this.attachmentPassword)
      return data.enabled ? availableAttachmentPassword : ''
    },
    handleDownload(data) {
      const requestData = {}
      requestData.model = this.historyTabModuleName[this.type]
      requestData.exportFormat = data.exportFormat
      requestData.enabled = data.enabled
      requestData.attachmentPassword = this.getAvailableAttachmentPassword(data)
      requestData.auditExportDataRest = {
        refModel: this.moduleName,
        refId: this.resourceId,
        ...(this.type === 'change_log'
          ? { componentType: this.componentType }
          : {}),
        startDate: this.startDate,
        endDate: this.endDate,
      }

      if (
        this.type === 'scan_history' &&
        this.discoveryMethod &&
        this.discoveryMethod !== 'all'
      ) {
        const quals = []
        quals.push(
          buildRelationalQualificationStructure(
            'discoveryMethod',
            'in',
            [this.discoveryMethod],
            'enum',
            'db'
          )
        )
        requestData.qualification = buildFlatQualificationStructure(quals)
      }
      return downloadApi(this.moduleName, requestData)
    },
  },
}
</script>
